// Which category a product belongs to, and the breadcrumb trail down to it — ONE
// rule shared by the prerender (scripts/prerender-og.mjs) and the React product
// page (src/pages/ProductDetail.tsx). The prerendered and the hydrated
// breadcrumb both stay in the document, so they must name the same path. They
// used to disagree on every product: the prerender took the first category in
// array order that named the type (usually the room), the React page the first
// one with a parent ("Bedroom Furniture" for a mirror, "Bathroom" for a vanity).
//
// Rule: among the categories whose query is a plain product_type list naming
// the type, the one listing the FEWEST types is the most specific ("Mirrors",
// one type, beats "Bedroom Furniture", twelve). A tie goes to the deeper
// category, then to the order in src/lib/categories.ts.

const TERM_RE = /product_type:\s*(?:"([^"]+)"|([^\s()]+))/g;
const norm = (s) => String(s ?? "").trim().toLowerCase();

/** Shown when no category names the product's type. */
export const UNCATEGORISED_CRUMB = { label: "All Categories", href: "/categories" };

/** Types of a plain `product_type:A OR product_type:"B"` query, else null. */
export function plainProductTypes(query) {
  if (!query) return null;
  const text = String(query);
  const types = [...text.matchAll(TERM_RE)].map((m) => (m[1] ?? m[2]).trim());
  if (types.length === 0) return null;
  // Anything besides the terms and OR (price bands, title: terms, AND) means the
  // query is a curated edit, not a product type's home.
  const rest = text.replace(TERM_RE, " ").replace(/\bOR\b/g, " ").replace(/[()]/g, " ").trim();
  return rest ? null : types;
}

/**
 * Lower-cased productType -> its trail of categories, room first, most specific
 * last. Rows need { slug, name, parent, query }; rows flagged skipCount or
 * useStaticFilters (All Products, Best Sellers, price and style edits) are
 * never a product's home.
 */
export function categoryTrailsByType(categories) {
  const bySlug = new Map(categories.map((c) => [c.slug, c]));
  const trailOf = (category) => {
    const trail = [];
    for (let at = category; at && !trail.includes(at); at = at.parent ? bySlug.get(at.parent) : null) {
      trail.unshift(at);
    }
    return trail;
  };

  const best = new Map();
  for (const c of categories) {
    if (c.skipCount || c.useStaticFilters) continue;
    const types = plainProductTypes(c.query);
    if (!types) continue;
    const candidate = { size: types.length, trail: trailOf(c) };
    for (const type of types) {
      const key = norm(type);
      const held = best.get(key);
      if (
        !held ||
        candidate.size < held.size ||
        (candidate.size === held.size && candidate.trail.length > held.trail.length)
      ) {
        best.set(key, candidate);
      }
    }
  }
  return new Map([...best].map(([key, { trail }]) => [key, trail]));
}

/** The trail for one product type; empty when no category names it. */
export function categoryTrailFor(trails, productType) {
  return trails.get(norm(productType)) ?? [];
}
